import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const pageLinks = [
  { label: "首页", to: "/" },
  { label: "关于我们", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

const anchorLinks = [
  { label: "常见问题", id: "problems" },
  { label: "解决方案", id: "solutions" },
  { label: "使用技巧", id: "tips" },
  { label: "常见问答", id: "faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHero = location.pathname === "/" && !scrolled;

  const goAnchor = (id: string) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 140);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/90 backdrop-blur-md shadow-soft" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link
          to="/"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2 whitespace-nowrap"
        >
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-xl text-lg ${
              onHero ? "bg-white/20 text-white" : "bg-primary-500 text-background-50"
            }`}
          >
            <i className="ri-pen-nib-fill" />
          </span>
          <span
            className={`font-display text-xl ${onHero ? "text-white" : "text-primary-700"}`}
          >
            笔笔漫画网
          </span>
          <span
            className={`ml-1 hidden rounded-full px-2 py-0.5 text-xs sm:inline-block ${
              onHero ? "bg-white/15 text-white/90" : "bg-secondary-100 text-secondary-900"
            }`}
          >
            帮助中心
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {anchorLinks.map((a) => (
            <button
              key={a.id}
              onClick={() => goAnchor(a.id)}
              className={`whitespace-nowrap rounded-full px-3.5 py-2 text-sm transition-colors ${
                onHero
                  ? "text-white/85 hover:bg-white/15 hover:text-white"
                  : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
              }`}
            >
              {a.label}
            </button>
          ))}
          <span className={`mx-2 h-5 w-px ${onHero ? "bg-white/20" : "bg-background-200"}`} />
          {pageLinks.slice(1).map((p) => (
            <Link
              key={p.to}
              to={p.to}
              className={`whitespace-nowrap rounded-full px-3.5 py-2 text-sm transition-colors ${
                location.pathname === p.to
                  ? onHero
                    ? "bg-white/20 text-white"
                    : "bg-primary-100 text-primary-700"
                  : onHero
                    ? "text-white/85 hover:bg-white/15 hover:text-white"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
              }`}
            >
              {p.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="ml-1 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            反馈问题
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="切换导航菜单"
          className={`flex h-10 w-10 items-center justify-center rounded-lg text-xl md:hidden ${
            onHero ? "bg-white/15 text-white" : "bg-background-100 text-foreground-900"
          }`}
        >
          <i className={open ? "ri-close-line" : "ri-menu-line"} />
        </button>
      </nav>

      {open && (
        <div className="mx-4 mb-3 rounded-2xl border border-background-200 bg-background-50 p-2 shadow-soft md:hidden">
          {anchorLinks.map((a) => (
            <button
              key={a.id}
              onClick={() => goAnchor(a.id)}
              className="block w-full whitespace-nowrap rounded-xl px-4 py-2.5 text-left text-sm text-foreground-700 hover:bg-background-100"
            >
              {a.label}
            </button>
          ))}
          <div className="my-1 h-px bg-background-200" />
          {pageLinks.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              onClick={() => setOpen(false)}
              className="block w-full whitespace-nowrap rounded-xl px-4 py-2.5 text-left text-sm text-foreground-700 hover:bg-background-100"
            >
              {p.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}